import React, { useState, useEffect } from "react";
import { Pencil, Trash2, ChevronDown, ChevronUp, Download, User } from "lucide-react";
import CustomDropdown from "../../Components/admin/CustomDropdown";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllPayments } from "../../redux/feeStructureSlice";

function Payments() {
  const dispatch = useDispatch();
  const { payments, loading } = useSelector((state) => state.feeStructures);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const [filters, setFilters] = useState({
    studentName: "",
    enrollmentNo: "",
    status: "",
  });

  useEffect(() => {
    dispatch(fetchAllPayments());
  }, [dispatch]);

  const toggleView = (index) => {
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "text-green-600";
      case "Pending":
        return "text-yellow-600";
      case "Overdue":
        return "text-red-600";
      default:
        return "";
    }
  };

  const filteredPayments = payments.filter(
    (payment) =>
      (filters.studentName === "" ||
        payment.studentName.toLowerCase().includes(filters.studentName.toLowerCase())) &&
      (filters.enrollmentNo === "" ||
        payment.enrollmentNo.toLowerCase().includes(filters.enrollmentNo.toLowerCase())) &&
      (filters.status === "" ||
        payment.feeBill.status.toLowerCase() === filters.status.toLowerCase())
  );

  return (
    <div>
      <div className="mb-4 flex gap-4 w-full">
        <div className="flex w-3/6">
          <input
            type="text"
            placeholder="Search Student Name..."
            className="border border-gray-300 rounded p-2 w-full"
            value={filters.studentName}
            onChange={(e) => handleFilterChange("studentName", e.target.value)}
          />
        </div>
        <div className="w-3/12">
          <input
            type="text"
            placeholder="Search Enrollment No..."
            className="border border-gray-300 rounded p-2 w-full"
            value={filters.enrollmentNo}
            onChange={(e) => handleFilterChange("enrollmentNo", e.target.value)}
          />
        </div>
        <div className="w-3/12">
          <CustomDropdown
            options={["Paid", "Pending", "Overdue"]}
            value={filters.status}
            onChange={(value) => handleFilterChange("status", value)}
            placeholder="Select Payment Status"
            className="w-full"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">Loading payments...</div>
      ) : (
        <div className="overflow-x-auto mt-6">
          <table className="table-auto w-full mt-6">
            <thead className="py-4 border-b font-bold">
              <tr className="bg-gray-100 border-b">
                <th className="p-2">Student</th>
                <th className="p-2">Enrollment No</th>
                <th className="p-2">Payment Date</th>
                <th className="p-2">Amount Paid</th>
                <th className="p-2">Billing Cycle</th>
                <th className="p-2">Status</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment, index) => (
                  <React.Fragment key={payment.paymentId}>
                    <tr className="hover:bg-gray-200 border-b">
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          {payment.picture ? (
                            <img 
                              src={payment.picture} 
                              alt={payment.studentName} 
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="h-4 w-4 text-gray-500" />
                            </div>
                          )}
                          <span>{payment.studentName}</span>
                        </div>
                      </td>
                      <td className="p-2">{payment.enrollmentNo}</td>
                      <td className="p-2">{formatDate(payment.paymentDate)}</td>
                      <td className="p-2">${payment.amountPaid.toLocaleString()}</td>
                      <td className="p-2">
                        {formatDate(payment.feeBill.billingCycleStartDate)} - {formatDate(payment.feeBill.billingCycleEndDate)}
                      </td>
                      <td className={`p-2 ${getStatusColor(payment.feeBill.status)}`}>
                        {payment.feeBill.status}
                      </td>
                      <td className="space-x-2 flex items-center p-2">
                        {payment.receiptAttachment ? (
                          <a 
                            href={payment.receiptAttachment} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                            title="Download Receipt"
                          >
                            <Download className="text-[#241763] w-4 h-4 group-hover:text-white" />
                          </a>
                        ) : (
                          <button 
                            className="bg-gray-200 p-2 rounded cursor-not-allowed opacity-50"
                            title="No Receipt Available"
                          >
                            <Download className="text-gray-500 w-4 h-4" />
                          </button>
                        )}
                        <div
                          onClick={() => toggleView(index)}
                          className="cursor-pointer bg-[#B6A5C9]/50 p-2 rounded hover:bg-[#241763]/60 group"
                        >
                          {expandedRowIndex === index ? (
                            <ChevronUp
                              size={16}
                              className="group-hover:text-white"
                            />
                          ) : (
                            <ChevronDown
                              size={16}
                              className="group-hover:text-white"
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRowIndex === index && (
                      <tr>
                        <td colSpan="7">
                          <div className="bg-gray-100 p-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-semibold">Fee Bill Details</h4>
                                <p><span className="font-medium">Bill ID:</span> {payment.feeBill.feeBillId}</p>
                                <p><span className="font-medium">Due Date:</span> {formatDate(payment.feeBill.dueDate)}</p>
                                <p><span className="font-medium">Total Fee:</span> ${payment.feeBill.totalFee.toLocaleString()}</p>
                                <p><span className="font-medium">Discount:</span> ${payment.feeBill.discountAmount.toLocaleString()}</p>
                                <p><span className="font-medium">Final Amount:</span> ${payment.feeBill.finalAmount.toLocaleString()}</p>
                                <p><span className="font-medium">Pending Amount:</span> ${Math.abs(payment.feeBill.pendingAmount).toLocaleString()}</p>
                              </div>
                              <div>
                                <h4 className="font-semibold">Payment Details</h4>
                                <p><span className="font-medium">Payment ID:</span> {payment.paymentId}</p>
                                <p><span className="font-medium">Student ID:</span> {payment.studentId}</p>
                                <p><span className="font-medium">Payment Date:</span> {formatDate(payment.paymentDate)}</p>
                                <p><span className="font-medium">Amount Paid:</span> ${payment.amountPaid.toLocaleString()}</p>
                                <p><span className="font-medium">Receipt:</span> {payment.receiptAttachment ? "Available" : "Not available"}</p>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center p-4">
                    No Payments Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Payments;